import _ from 'underscore';
import React, { useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  AdsStudioAssetGridCell,
  GridSectionName,
  SectionTilesGrid,
  DEFAULT_PAGE_SIZE,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';

export const VideoGridView = ({
  i18n,
  dataService,
  logger,
  onFailedToLoadData,
  searchText,
  navigation,
  permissions,
}) => {
  const {
    assets,
    totalAssetCount,
    loadMoreData,
    renameAsset,
  } = dataService;

  const videosGridSection = useMemo(() => ({
    sectionName: GridSectionName.Asset,
    sectionClassName: 'ads-studio-video-section',
    itemKey: 'id',
    items: assets,
    totalItemCount: totalAssetCount,
    config: {
      scrollContainer: '[role="main"], .app-content-container-col',
      minItemHeight: 42,
    },
  }), [assets, totalAssetCount]);

  const sectionGridData = useMemo(
    () => ([videosGridSection]),
    [videosGridSection]
  );

  const shouldShowExternalLink = useMemo(() => {
    const isMacEnv = new URLSearchParams(document.location.search).get('isMacEnv');
    return isMacEnv === 'true';
  }, []);

  const onRenderCell = useCallback(({ item }) => {
    const { _index, id, folderAssetType } = item;

    const onCellDetailsClick = () => {
      navigation.gotoAssetDetails({
        assetId: id, assets, totalAssetCount, folderAssetType, loadMoreData, renameAsset,
      });
    };

    /* istanbul ignore next */
    const onEditClick = () => {
      if (item && item.id && item.metaData) {
        navigation.gotoEditVideoPage({ videoId: item.id });
      }
    };

    return (
      <AdsStudioAssetGridCell
        item={item}
        assets={assets}
        onDetailsClick={onCellDetailsClick}
        onEditClick={onEditClick}
        index={_index}
        i18n={i18n}
        highlightedText={searchText}
        logger={logger}
        permissions={permissions}
        shouldShowExternalLink={shouldShowExternalLink}
      />
    );
  }, [assets, i18n, loadMoreData, logger, navigation, renameAsset, searchText, totalAssetCount, shouldShowExternalLink]);

  const loadNextPage = useCallback(() => {
    loadMoreData({ count: DEFAULT_PAGE_SIZE }).catch((err) => {
      onFailedToLoadData(err);
    });
  }, [loadMoreData, onFailedToLoadData]);

  useEffect(() => {
    if (navigation) {
      navigation.updateAssetDetailsParams({ assets });
    }
  }, [assets, navigation]);

  return (
    <SectionTilesGrid
      sectionData={sectionGridData}
      onRenderCell={onRenderCell}
      loadNextPage={loadNextPage}
    />
  );
};

VideoGridView.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  dataService: PropTypes.shape({}),
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  onFailedToLoadData: PropTypes.func,
  searchText: PropTypes.string,
  navigation: PropTypes.shape({}).isRequired,
};

VideoGridView.defaultProps = {
  dataService: {},
  onFailedToLoadData: _.noop,
  searchText: '',
};
